import { useEffect, useState } from "react";
import StarRateIcon from "@mui/icons-material/StarRate";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import { Skeleton, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import getServices from "../../Services/get-service";
import postService from "../../Services/post-service";
import { toast } from "react-toastify";

function Rentals() {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchRentals = () => {
    setLoading(true);
    getServices
      .getApartmentBuyLater(0, 10)
      .then((res) => {
        setLoading(false);
        if (res?.data?.statusCode === 200) {
          setRentals(res.data.data.savedApartmentClass || []);
        } else {
          toast.error(
            res?.data?.description || "Failed to fetch saved rentals",
          );
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  };

  useEffect(() => {
    fetchRentals();
  }, []);

  const handleExplore = () => {
    navigate("/rental/active");
  };

  const formatPrice = (price) => {
    if (!price) return "NGN0";
    return `NGN${Number(price).toLocaleString()}`;
  };

  return (
    <div className="w-full flex flex-col justify-between">
      {loading ? (
        <div className="mt-4">
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="border border-gray-100 rounded-2xl bg-white overflow-hidden shadow-sm p-3 flex flex-col"
              >
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={180}
                  sx={{ borderRadius: "12px", mb: 1.5 }}
                />
                <div className="flex justify-between items-start mb-1">
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="circular" width={20} height={20} />
                </div>
                <div className="flex items-center gap-4 mb-4 mt-1">
                  <Skeleton variant="text" width="30%" height={16} />
                  <Skeleton variant="text" width="40%" height={16} />
                </div>
                <div className="flex justify-between items-center mt-auto pt-2">
                  <Skeleton
                    variant="rounded"
                    width={70}
                    height={24}
                    sx={{ borderRadius: "6px" }}
                  />
                  <Skeleton variant="text" width="35%" height={20} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : rentals.length > 0 ? (
        <div className="mt-4 cursor-pointer">
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rentals.map((rental) => (
              <div
                key={rental.id}
                className="border border-gray-100 rounded-2xl bg-white overflow-hidden shadow-sm p-3 flex flex-col cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => {
                  navigate("/rental/active", {
                    state: { selectedSavedApartment: rental },
                  });
                }}
              >
                <div className="relative mb-3">
                  <img
                    src={rental?.apartmentClassImages?.[0]?.imageUrl}
                    alt={rental.name || ""}
                    className="w-full h-[180px] object-cover rounded-xl"
                  />
                </div>

                <div className="flex justify-between items-start mb-1">
                  <h3 className="text-[#1E222B] font-[700] text-[16px] leading-tight flex-1 pr-2">
                    {rental.apartmentName || ""}
                  </h3>
                  {rental.isRemoving ? (
                    <CircularProgress size={20} className="text-[#00BC67]" />
                  ) : (
                    <BookmarkIcon
                      className="text-[#00BC67] cursor-pointer"
                      fontSize="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRentals((prev) =>
                          prev.map((r) =>
                            r.id === rental.id ? { ...r, isRemoving: true } : r,
                          ),
                        );
                        postService
                          .removeApartmentFromBookmark(
                            rental.apartmentClassId || rental.id,
                          )
                          .then((res) => {
                            if (res?.data?.statusCode === 200) {
                              toast.success(
                                "Apartment removed from saved list",
                              );
                              fetchRentals();
                            } else {
                              toast.error(res?.data?.description);
                              setRentals((prev) =>
                                prev.map((r) =>
                                  r.id === rental.id
                                    ? { ...r, isRemoving: false }
                                    : r,
                                ),
                              );
                            }
                          })
                          .catch((err) => {
                            toast.error(err?.response?.data?.description);
                            setRentals((prev) =>
                              prev.map((r) =>
                                r.id === rental.id
                                  ? { ...r, isRemoving: false }
                                  : r,
                              ),
                            );
                          });
                      }}
                    />
                  )}
                </div>

                <div className="flex items-center gap-4 mb-4 mt-1">
                  {/* <p className="flex items-center gap-1 text-[12px] font-[500] text-[#1E222B]">
                    <StarRateIcon
                      className="text-[#A3D133]"
                      sx={{ fontSize: 16 }}
                    />
                    {"4.0"} ({22})
                  </p> */}
                  <p className="text-[#1E222B] text-[14px] flex items-center gap-1">
                    <FmdGoodOutlinedIcon
                      fontSize="small"
                      className="text-[#808285]"
                    />
                    {rental?.location} • {rental?.streetName}
                  </p>
                </div>

                <div className="flex justify-between items-center mt-auto pt-2">
                  <span className="px-3 py-1 rounded-md text-[11px] font-[700] tracking-wide text-[#00BC67] bg-[#E6F8F0]">
                    Available
                  </span>
                  <p className="text-[#002615] font-[700] text-[14px]">
                    {formatPrice(rental.price)}
                    <span className="text-[#808285] font-[500] text-[14px]">
                      / year
                    </span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col items-center justify-center mt-20">
            <div className="bg-white p-4 rounded-full shadow-sm mb-4">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#00BC67"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No Saved Rentals Yet
            </h3>
            <p className="text-[14px] text-[#808285] mb-6 text-center max-w-sm">
              Tap the bookmark icon on any rental listing to save it here for
              easy access.
            </p>
          </div>
          <div className="flex justify-center my-4 w-full">
            <button
              onClick={() => handleExplore()}
              className="px-10 py-2 rounded-md bg-[#00B172] hover:bg-[#009b63] text-white font-medium text-[16px] transition-colors"
            >
              Explore more rentals
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Rentals;
